import { useEffect, useState } from 'react';
import authService from '../../services/auth';

/** @typedef {'checking' | 'authenticated' | 'anonymous'} SessionStatus */

/**
 * The routes render only once the page load has read the shared session (see `checkSessionOnPageLoad`), so a tab
 * never shows a user the cookie no longer holds. Later routes of the same page load reuse that answer.
 * @param {boolean} [skip] `true` to treat the tab as signed out without reading the session
 * @returns {SessionStatus}
 */
const useSessionBootstrap = (skip = false) => {
    /** @type {[SessionStatus, (status: SessionStatus) => void]} */
    const [status, setStatus] = useState(() => {
        if (skip) {
            return 'anonymous';
        }
        if (authService.pageLoadResult === null) {
            return 'checking';
        }
        return authService.pageLoadResult && authService.isLoggedIn() ? 'authenticated' : 'anonymous';
    });

    useEffect(() => {
        if (status !== 'checking') {
            return undefined;
        }

        let isActive = true;
        authService.checkSessionOnPageLoad().then((isAuthenticated) => {
            if (isActive) {
                setStatus(isAuthenticated ? 'authenticated' : 'anonymous');
            }
        });

        return () => {
            isActive = false;
        };
    }, [status]);

    return status;
};

export default useSessionBootstrap;
